import React, { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import api from "~/services/api";
import { formatter } from "~/functions";
import { getData } from "~/functions/gridFunctions";
import {
  theme,
  field,
  primaryButton,
  ghostButton,
  label,
  spread as row,
} from "../../Gravity Conciliation/Preview/modalStyles";

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

function inputDate(value) {
  if (!value) return format(new Date(), "yyyy-MM-dd");
  try {
    return format(parseISO(value), "yyyy-MM-dd");
  } catch (err) {
    return format(new Date(), "yyyy-MM-dd");
  }
}

function Picker({
  title,
  route,
  type,
  required,
  extraParams,
  value,
  onChange,
  describe,
  disabled,
  disabledHint,
}) {
  const [active, setActive] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  async function loadData(searchValue = null) {
    setLoading(true);
    try {
      const data = await getData(route, {
        limit: 10,
        search: searchValue ? { value: searchValue } : null,
        type,
        extraParams,
      });
      setRows((data || []).filter(Boolean));
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (active) loadData();
  }, [active]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <span style={label}>
        {title}
        {required ? " *" : ""}
      </span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          marginTop: 4,
        }}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => setActive(!active)}
          style={{
            ...ghostButton,
            padding: 8,
            display: "flex",
            opacity: disabled ? 0.4 : 1,
          }}
        >
          {active ? <X size={14} /> : <Search size={14} />}
        </button>
        <div
          style={{
            ...field,
            flex: 1,
            width: "auto",
            backgroundColor: theme.subtle,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value ? describe(value) : disabled ? disabledHint || "-" : "-"}
        </div>
      </div>
      {active ? (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 20,
            backgroundColor: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            width: "100%",
            maxHeight: 200,
            overflowY: "auto",
            marginTop: 4,
          }}
        >
          <input
            type="text"
            autoFocus
            placeholder="Search..."
            onChange={(event) => loadData(event.target.value)}
            style={{
              ...field,
              border: "none",
              borderBottom: `1px solid ${theme.border}`,
              borderRadius: 0,
            }}
          />
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 12,
                color: theme.muted,
              }}
            >
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{ padding: 12, color: theme.muted, textAlign: "center" }}
            >
              No results.
            </div>
          ) : (
            rows.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                onClick={() => {
                  onChange(candidate);
                  setActive(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: 8,
                  border: "none",
                  borderBottom: `1px solid ${theme.subtle}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {describe(candidate)}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function CreateEntryTab({
  item,
  isDebit = true,
  bankAccountId,
  onCreated,
  onCancel,
}) {
  const [merchant, setMerchant] = useState(null);
  const [chartOfAccount, setChartOfAccount] = useState(null);
  const [costCenter, setCostCenter] = useState(null);
  const [dueDate, setDueDate] = useState(inputDate(item?.date));
  const [memo, setMemo] = useState(item?.description || "");
  const [saving, setSaving] = useState(false);

  const [methods, setMethods] = useState([]);
  const [methodId, setMethodId] = useState("");
  const [methodsLoaded, setMethodsLoaded] = useState(false);

  useEffect(() => {
    async function loadMethods() {
      try {
        const data = await getData("paymentmethods", {
          limit: 50,
          extraParams: bankAccountId
            ? { bankaccount_id: bankAccountId }
            : null,
        });
        const list = (data || []).filter(Boolean).filter((method) =>
          bankAccountId
            ? method.bankAccount?.id === bankAccountId ||
              method.bankaccount_id === bankAccountId
            : true
        );
        setMethods(list);
        if (list.length === 1) setMethodId(list[0].id);
      } catch (err) {
        setMethods([]);
      } finally {
        setMethodsLoaded(true);
      }
    }
    loadMethods();
  }, [bankAccountId]);

  const amount = Math.abs(Number(item?.amount || 0));
  const needsMethodChoice = methods.length > 1;
  const dueDateLabel = isDebit ? "Due Date" : "Expected Date";
  const submitLabel = isDebit
    ? "Create Payable and Reconcile"
    : "Create Receivable and Reconcile";

  async function handleSubmit() {
    if (!merchant) {
      toast("Select the merchant.", { type: "warning", autoClose: 2000 });
      return;
    }
    if (!chartOfAccount) {
      toast("Select the chart of account.", { type: "warning", autoClose: 2000 });
      return;
    }
    if (needsMethodChoice && !methodId) {
      toast("Select the payment method.", { type: "warning", autoClose: 2000 });
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post(
        `/bankconciliations-items/${item.id}/create-and-link`,
        {
          merchant_id: merchant.id,
          chartofaccount_id: chartOfAccount.id,
          ...(costCenter?.id ? { costcenter_id: costCenter.id } : {}),
          ...(methodId ? { paymentmethod_id: methodId } : {}),
          due_date: dueDate,
          ...(memo.trim() ? { memo: memo.trim() } : {}),
        }
      );
      toast(
        `${
          data?.side === "Receivable" ? "Receivable" : "Payable"
        } created and reconciled — ${formatter.format(
          Number(data?.amount || amount)
        )}.`,
        { autoClose: 2500 }
      );
      onCreated();
    } catch (err) {
      toast(err?.response?.data?.error || "Could not create the entry.", {
        type: "error",
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Picker
        title="Merchant"
        route="merchants"
        required
        value={merchant}
        onChange={setMerchant}
        describe={(candidate) => candidate.name}
      />

      <Picker
        title="Chart of Account"
        route="chartofaccounts"
        type={isDebit ? "expenses" : "receipts"}
        required
        extraParams={merchant ? { merchant_id: merchant.id } : null}
        disabled={!merchant}
        disabledHint="Select the merchant first"
        value={chartOfAccount}
        onChange={setChartOfAccount}
        describe={(candidate) =>
          `${candidate.code || ""} ${candidate.name || ""}`.trim()
        }
      />

      <Picker
        title="Cost Center"
        route="costcenters"
        value={costCenter}
        onChange={setCostCenter}
        describe={(candidate) =>
          `${candidate.code || ""} ${candidate.name || ""}`.trim()
        }
      />

      <div style={{ width: "100%" }}>
        <span style={label}>{dueDateLabel}</span>
        <input
          type="date"
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          style={{ ...field, marginTop: 4 }}
        />
      </div>

      <div style={{ width: "100%" }}>
        <span style={label}>Memo</span>
        <input
          type="text"
          value={memo}
          onChange={(event) => setMemo(event.target.value)}
          style={{ ...field, marginTop: 4 }}
        />
      </div>

      {needsMethodChoice ? (
        <div style={{ width: "100%" }}>
          <span style={label}>Payment Method *</span>
          <select
            value={methodId}
            onChange={(event) => setMethodId(event.target.value)}
            style={{ ...field, marginTop: 4 }}
          >
            <option value="">Select...</option>
            {methods.map((method) => (
              <option key={method.id} value={method.id}>
                {method.description}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div
        style={{
          borderTop: `1px solid ${theme.border}`,
          paddingTop: 12,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={row}>
          <span style={label}>Amount</span>
          <strong>{formatter.format(amount)}</strong>
        </div>
        <div style={row}>
          <span style={label}>Date</span>
          <strong>{safeDate(item?.date)}</strong>
        </div>
        <div style={row}>
          <span style={label}>Method</span>
          <strong>
            {methodsLoaded
              ? methods.find((method) => method.id === methodId)?.description ||
                (methods.length === 1
                  ? methods[0].description
                  : needsMethodChoice
                  ? "-"
                  : "Resolved by the account")
              : "..."}
          </strong>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button type="button" onClick={onCancel} style={ghostButton}>
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={handleSubmit}
          style={{ ...primaryButton, opacity: saving ? 0.5 : 1 }}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
